/**
 * CSV import (PLAN §9 Mode C, §11).
 *
 * Read in the browser and converted to the same pair list every other mode
 * produces, so the server never handles an upload at all.
 *
 * Written against what spreadsheets actually export rather than against the
 * happy path: a byte-order mark in front of the first field, semicolons because
 * the machine's locale uses a comma for decimals, quoted fields containing the
 * delimiter, doubled quotes inside them, and a newline inside a quoted cell
 * that would otherwise look like the end of the row.
 *
 * Pure by construction (PLAN §4).
 */

import { pairsFromRows, type TableOutcome, type TableProblem } from './table.ts';

export interface CsvRecord {
  /** 1-based physical line the record starts on. A quoted newline does not renumber it. */
  readonly line: number;
  readonly fields: readonly string[];
}

/** The delimiters worth sniffing for: comma, the locale semicolon, and tab. */
export const CSV_DELIMITERS = [',', ';', '\t'] as const;

export type CsvProblem = TableProblem;

export interface CsvOutcome extends TableOutcome {
  /** Which of `CSV_DELIMITERS` the file turned out to use. */
  readonly delimiter: string;
}

const BOM = '﻿';

/**
 * Splits CSV text into records, RFC 4180 style.
 *
 * A quote only opens a field at the start of one; a `"` in the middle of an
 * unquoted field is data, because that is what a spreadsheet writing
 * `5" pipe` produces and refusing it would help nobody.
 */
export function parseCsv(text: string, delimiter: string): readonly CsvRecord[] {
  const source = text.startsWith(BOM) ? text.slice(BOM.length) : text;

  const records: CsvRecord[] = [];
  let fields: string[] = [];
  let field = '';
  let quoted = false;
  let fieldStarted = false;
  let line = 1;
  let recordLine = 1;

  const endField = (): void => {
    fields.push(field);
    field = '';
    quoted = false;
    fieldStarted = false;
  };

  const endRecord = (): void => {
    endField();
    records.push({ line: recordLine, fields });
    fields = [];
  };

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index] as string;

    if (quoted) {
      if (character === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (source[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
        }
        continue;
      }
      // Newlines inside quotes are data, but the file's line count still moves.
      if (character === '\n') line += 1;
      field += character;
      continue;
    }

    if (character === '"' && !fieldStarted) {
      quoted = true;
      fieldStarted = true;
      continue;
    }

    if (character === delimiter) {
      endField();
      continue;
    }

    if (character === '\r' || character === '\n') {
      // \r\n is one break, not two.
      if (character === '\r' && source[index + 1] === '\n') index += 1;
      line += 1;
      endRecord();
      recordLine = line;
      continue;
    }

    fieldStarted = true;
    field += character;
  }

  // A file that does not end in a newline still ends in a record — unless
  // nothing at all was pending, which is what a trailing newline leaves behind.
  if (field !== '' || fields.length > 0) endRecord();

  return records.map((record) => ({
    line: record.line,
    fields: record.fields.map((value) => value.trim()),
  }));
}

/**
 * Picks the delimiter by counting candidates in the first line that is not
 * inside quotes.
 *
 * Sniffing is safe here in a way it is not for paired lines: guessing wrong
 * produces one wide column rather than a plausible-looking wrong pairing, and
 * one column is refused a few lines below.
 */
export function sniffDelimiter(text: string): string {
  const firstLine = (text.startsWith(BOM) ? text.slice(BOM.length) : text).split(/\r?\n/)[0] ?? '';

  let best = CSV_DELIMITERS[0] as string;
  let bestCount = 0;
  for (const candidate of CSV_DELIMITERS) {
    const count = countOutsideQuotes(firstLine, candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;

  for (const character of line) {
    if (character === '"') quoted = !quoted;
    else if (!quoted && character === delimiter) count += 1;
  }
  return count;
}

/**
 * Reads a CSV into pairs. Which column is which, and whether the first row is a
 * header, is `table.ts`'s job — the same code the XLSX reader uses, so one
 * spreadsheet imports identically whichever format it was saved in.
 */
export function parseCsvPairs(text: string): CsvOutcome {
  const delimiter = sniffDelimiter(text);
  const outcome = pairsFromRows(parseCsv(text, delimiter));
  return { ...outcome, delimiter };
}
